import { useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Loader2, Heart, ArrowLeft } from "lucide-react";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

export default function ReviewPage() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (rating < 1) { toast.error("Please pick a star rating"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/review`, { rating, comment }, { params: { token } });
      setDone(true);
      toast.success("Thanks for your feedback!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-yellow-100 p-4">
        <Card className="max-w-md w-full" data-testid="review-thanks">
          <CardContent className="p-8 text-center">
            <Heart className="w-14 h-14 text-rose-500 mx-auto mb-3" />
            <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
            <p className="text-muted-foreground">Your {rating}-star feedback helps us serve J1 students better.</p>
            <Link to="/" className="block mt-5"><Button className="rounded-full bg-green-600 hover:bg-green-700"><ArrowLeft className="w-4 h-4 mr-2" /> Home</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-100 py-10 px-4">
      <div className="max-w-md mx-auto">
        <Card className="shadow-xl border-0" data-testid="review-card">
          <CardContent className="p-6 md:p-8 text-center">
            <div className="bg-amber-100 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
              <Star className="w-8 h-8 text-amber-600 fill-amber-500" />
            </div>
            <h1 className="text-2xl font-bold mb-1">How was your trip?</h1>
            <p className="text-sm text-muted-foreground mb-5">Easy Transportation J1</p>

            <div className="flex justify-center gap-2 mb-5" data-testid="star-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  className="transition-transform hover:scale-110"
                  data-testid={`star-${n}`}
                  aria-label={`${n} star`}
                >
                  <Star className={`w-12 h-12 ${(hover || rating) >= n ? "fill-amber-400 text-amber-500" : "text-gray-300"}`} />
                </button>
              ))}
            </div>

            <Textarea
              placeholder="Tell us more (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[100px] rounded-xl mb-4"
              data-testid="review-comment"
            />

            <Button
              onClick={submit}
              disabled={submitting || rating < 1}
              className="w-full h-12 rounded-full bg-amber-600 hover:bg-amber-700 font-semibold"
              data-testid="review-submit-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Submit Review"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
